import type { Edge } from '@xyflow/react';
import type { OrgNode } from '@pms/shared';
import type { OrgFlowNode, OrgFlowNodeData } from './types';

/** Callbacks + view state needed to build node `data` without re-deriving per node. */
interface BuildContext {
  collapsedIds: Set<string>;
  selectedId: string | null;
  matchedIds: Set<string>;
  searchActive: boolean;
  onSelect: (id: string) => void;
  onToggleCollapse: (id: string) => void;
}

/**
 * Flattens the nested OrgNode tree into React Flow nodes + edges.
 * A collapsed node is included but its subtree is excluded entirely, which keeps
 * the dagre layout tight (no empty gaps for hidden descendants).
 * Positions are placeholders here — dagre assigns real coordinates downstream.
 */
export function treeToGraph(
  roots: OrgNode[],
  ctx: BuildContext,
): { nodes: OrgFlowNode[]; edges: Edge[] } {
  const nodes: OrgFlowNode[] = [];
  const edges: Edge[] = [];

  const walk = (node: OrgNode, parentId: string | null) => {
    const children = node.children ?? [];
    const hasChildren = children.length > 0;
    const collapsed = ctx.collapsedIds.has(node.id);

    const data: OrgFlowNodeData = {
      node,
      hasChildren,
      collapsed,
      childCount: children.length,
      isSelected: ctx.selectedId === node.id,
      isMatch: ctx.matchedIds.has(node.id),
      searchActive: ctx.searchActive,
      onSelect: ctx.onSelect,
      onToggleCollapse: ctx.onToggleCollapse,
    };

    nodes.push({ id: node.id, type: 'orgNode', position: { x: 0, y: 0 }, data });

    if (parentId) {
      edges.push({
        id: `${parentId}->${node.id}`,
        source: parentId,
        target: node.id,
        type: 'smoothstep',
      });
    }

    if (hasChildren && !collapsed) {
      for (const child of children) walk(child, node.id);
    }
  };

  for (const root of roots) walk(root, null);
  return { nodes, edges };
}

/** Every node id in the tree that has at least one child (used by "Collapse all"). */
export function collectAllParentIds(roots: OrgNode[]): Set<string> {
  const ids = new Set<string>();
  const walk = (node: OrgNode) => {
    if (node.children?.length) {
      ids.add(node.id);
      for (const child of node.children) walk(child);
    }
  };
  for (const root of roots) walk(root);
  return ids;
}

/** Direct (own-name) case-insensitive matches for the search term. */
export function collectMatchIds(roots: OrgNode[], query: string): Set<string> {
  const ids = new Set<string>();
  const q = query.trim().toLowerCase();
  if (!q) return ids;
  const walk = (node: OrgNode) => {
    if (node.name.toLowerCase().includes(q)) ids.add(node.id);
    for (const child of node.children ?? []) walk(child);
  };
  for (const root of roots) walk(root);
  return ids;
}

/**
 * Collects the ancestor ids of every node in `targetIds` (excluding the targets
 * themselves). Used to auto-reveal matched nodes that sit inside collapsed branches.
 */
export function collectAncestorIds(roots: OrgNode[], targetIds: Set<string>): Set<string> {
  const ancestors = new Set<string>();
  if (targetIds.size === 0) return ancestors;

  const record = (node: OrgNode, trail: string[]) => {
    if (targetIds.has(node.id)) {
      for (const id of trail) ancestors.add(id);
    }
    for (const child of node.children ?? []) record(child, [...trail, node.id]);
  };
  for (const root of roots) record(root, []);
  return ancestors;
}
